import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { loadLocalFile, pruneLocalFiles } from '../lib/localMedia';

export type LayoutId = 'cinematic' | 'command' | 'bento' | 'split';
export type ThemeId = 'dark' | 'light' | 'oled' | 'amber' | 'contrast';
export type TransitionId = 'fade' | 'slide' | 'dissolve' | 'kenburns';

export interface MediaItem {
  id: string;
  url: string;
  kind: 'image' | 'video';
  title?: string;
  /** A file from this device: stored in IndexedDB, shown via an object URL, never synced. */
  local?: boolean;
}

export interface Location {
  name: string;
  latitude: number;
  longitude: number;
}

export interface FeedSource {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
}

export interface CalendarSource extends FeedSource {
  color: string;
}

/** A cloud folder whose photos/videos join the slideshow (expanded at runtime). */
export interface CloudFolder {
  id: string;
  provider: 'onedrive';
  folderId: string;
  name: string;
}

export interface Settings {
  layout: LayoutId;
  theme: ThemeId;
  media: {
    items: MediaItem[];
    cloudFolders: CloudFolder[];
    intervalSec: number;
    transition: TransitionId;
    shuffle: boolean;
    overlayColor: string;
    overlayOpacity: number; // 0..1
  };
  clock: { hour12: boolean; showSeconds: boolean; timeZone: string; showDate: boolean };
  weather: { enabled: boolean; location: Location | null; unit: 'celsius' | 'fahrenheit' };
  calendar: { enabled: boolean; sources: CalendarSource[]; days: number; outlook: boolean };
  news: { enabled: boolean; sources: FeedSource[]; speed: number };
  lists: { enabled: boolean };
  customCss: string;
}

export const LAYOUTS: Array<{ id: LayoutId; name: string; description: string }> = [
  { id: 'cinematic', name: 'Cinematic Fullscreen', description: 'Edge-to-edge media with a minimal clock and ticker.' },
  { id: 'command', name: 'Command Center', description: 'Media with a stacked column of widgets on the right.' },
  { id: 'bento', name: 'Modular Bento Grid', description: 'Glass tiles arranged in a grid over your media.' },
  { id: 'split', name: 'Split Sidebar', description: 'A solid sidebar of widgets beside the media.' },
];

export const THEMES: Array<{ id: ThemeId; name: string }> = [
  { id: 'dark', name: 'Modern Dark' },
  { id: 'light', name: 'Light' },
  { id: 'oled', name: 'OLED Pure Black' },
  { id: 'amber', name: 'Warm Amber' },
  { id: 'contrast', name: 'High Contrast' },
];

export const TRANSITIONS: Array<{ id: TransitionId; name: string }> = [
  { id: 'fade', name: 'Fade' },
  { id: 'dissolve', name: 'Cross-dissolve' },
  { id: 'slide', name: 'Slide' },
  { id: 'kenburns', name: 'Zoom (Ken Burns)' },
];

export const INTERVAL_MIN = 5;
export const INTERVAL_MAX = 3600;

export const defaultSettings: Settings = {
  layout: 'cinematic',
  theme: 'dark',
  media: {
    items: [],
    cloudFolders: [],
    intervalSec: 30,
    transition: 'kenburns',
    shuffle: true,
    overlayColor: '#000000',
    overlayOpacity: 0.25,
  },
  clock: { hour12: false, showSeconds: false, timeZone: '', showDate: true },
  weather: { enabled: true, location: null, unit: 'celsius' },
  calendar: { enabled: true, sources: [], days: 7, outlook: true },
  news: {
    enabled: true,
    sources: [
      { id: 'bbc', name: 'BBC News', url: 'https://feeds.bbci.co.uk/news/rss.xml', enabled: true },
      { id: 'guardian', name: 'The Guardian', url: 'https://www.theguardian.com/uk/rss', enabled: false },
    ],
    speed: 60,
  },
  lists: { enabled: true },
  customCss: '',
};

export const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

interface SettingsStore extends Settings {
  update: <K extends keyof Settings>(key: K, patch: Partial<Settings[K]> | Settings[K]) => void;
  replaceAll: (s: Partial<Settings>) => void;
  reset: () => void;
}

function merge<T>(current: T, patch: Partial<T> | T): T {
  if (typeof current === 'object' && current !== null && !Array.isArray(current) && typeof patch === 'object') {
    return { ...current, ...patch };
  }
  return patch as T;
}

/** Shape safe to send to the server: device-only files are never uploaded. */
export function syncableSettings(s: Settings): Settings {
  return { ...s, media: { ...s.media, items: s.media.items.filter((i) => !i.local) } };
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Fills in defaults one level deep, so settings saved by an older version (or another
 * screen on an older version) gain new fields instead of leaving them undefined.
 */
export function withDefaults(incoming: Partial<Settings> | null | undefined): Settings {
  const out: Record<string, unknown> = { ...defaultSettings };
  for (const [k, v] of Object.entries(incoming ?? {})) {
    if (!(k in defaultSettings) || v === undefined) continue;
    const d = (defaultSettings as unknown as Record<string, unknown>)[k];
    out[k] = isPlainObject(d) && isPlainObject(v) ? { ...d, ...v } : v;
  }
  return out as unknown as Settings;
}

export function pickSettings(s: Settings): Settings {
  const { layout, theme, media, clock, weather, calendar, news, lists, customCss } = s;
  return { layout, theme, media, clock, weather, calendar, news, lists, customCss };
}

export const useSettings = create<SettingsStore>()(
  persist(
    (set) => ({
      ...defaultSettings,
      update: (key, patch) => set((s) => ({ [key]: merge(s[key], patch) }) as Partial<SettingsStore>),
      replaceAll: (incoming) =>
        set((s) => {
          const next = withDefaults(incoming);
          // Keep this device's local files when applying settings from another screen.
          const local = s.media.items.filter((i) => i.local);
          return { ...next, media: { ...next.media, items: [...next.media.items.filter((i) => !i.local), ...local] } };
        }),
      reset: () => {
        void pruneLocalFiles(new Set()).catch(() => undefined);
        set({ ...defaultSettings });
      },
    }),
    {
      name: 'kcl-canvas-settings',
      version: 1,
      merge: (persisted, current) => ({ ...current, ...withDefaults(persisted as Partial<Settings>) }),
      // Local files are kept in IndexedDB; their object URLs die with the page, so persist
      // the item without its URL and rebuild it in restoreLocalMedia().
      partialize: (s) => {
        const picked = pickSettings(s);
        return { ...picked, media: { ...picked.media, items: picked.media.items.map((i) => (i.local ? { ...i, url: '' } : i)) } };
      },
    },
  ),
);

let restoring: Promise<void> | null = null;

/** Recreates object URLs for device files after a reload; drops items whose file is gone. */
export function restoreLocalMedia(): Promise<void> {
  return (restoring ??= doRestoreLocalMedia());
}

async function doRestoreLocalMedia(): Promise<void> {
  const items = useSettings.getState().media.items;
  const local = items.filter((i) => i.local && !i.url);
  if (!local.length) {
    void pruneLocalFiles(new Set(items.filter((i) => i.local).map((i) => i.id))).catch(() => undefined);
    return;
  }
  const restored = new Map<string, string>();
  await Promise.all(
    local.map(async (i) => {
      const blob = await loadLocalFile(i.id).catch(() => undefined);
      if (blob) restored.set(i.id, URL.createObjectURL(blob));
    }),
  );
  const current = useSettings.getState().media.items;
  useSettings.getState().update('media', {
    items: current
      .map((i) => (i.local && !i.url ? (restored.has(i.id) ? { ...i, url: restored.get(i.id)! } : null) : i))
      .filter((i): i is MediaItem => i !== null),
  });
}
